import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AppConfig } from '@system-atlas/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { CreditFooter } from './CreditFooter';
import { Badge, Button } from './ui';

function LanguageSwitch() {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage ?? 'de';
  return (
    <div className="flex items-center gap-1 text-sm">
      {(['de', 'en'] as const).map((lng) => (
        <button
          key={lng}
          type="button"
          onClick={() => void i18n.changeLanguage(lng)}
          className={
            current === lng
              ? 'rounded px-2 py-1 bg-slate-900 text-white dark:bg-white dark:text-slate-900'
              : 'rounded px-2 py-1 hover:bg-slate-300/50'
          }
        >
          {lng.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isGroupManager, setIsGroupManager] = useState(false);

  useEffect(() => {
    api.getAppConfig().then(setAppConfig).catch(() => undefined);
  }, []);

  // Gruppen-Manager (Rolle `user`) bekommen einen eigenen Navigationslink zur
  // Benutzergruppen-Verwaltung; Admins erreichen sie ueber die Benutzerverwaltung.
  useEffect(() => {
    if (user?.role !== 'user') {
      setIsGroupManager(false);
      return;
    }
    api
      .listUserGroups()
      .then(({ groups }) => setIsGroupManager(groups.some((g) => g.isManager)))
      .catch(() => undefined);
  }, [user?.role]);

  // Mobiles Menü bei jedem Seitenwechsel schließen.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Rollenbasierte Navigationslinks – einmal definiert, in Desktop- und Mobilansicht genutzt.
  const navLinks: { to: string; label: string }[] = user
    ? [
        { to: '/', label: t('nav.drawings') },
        ...(user.role === 'user' && isGroupManager
          ? [{ to: '/user-groups', label: t('nav.userGroups') }]
          : []),
        ...(user.role === 'admin' || user.role === 'superadmin'
          ? [
              { to: '/admin/users', label: t('nav.users') },
              { to: '/admin/images', label: t('nav.images') },
            ]
          : []),
        ...(user.role === 'superadmin' ? [{ to: '/admin/settings', label: t('nav.settings') }] : []),
        { to: '/security', label: t('nav.security') },
      ]
    : [];

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-50 to-slate-200 text-slate-900 dark:from-slate-900 dark:to-slate-950 dark:text-slate-100">
      <header className="border-b border-slate-200/70 dark:border-slate-700/70">
        <div className="flex items-center justify-between px-6 py-3">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            {t('app.name')}
          </Link>

          {/* Desktop-Navigation */}
          <div className="hidden items-center gap-4 md:flex">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-medium hover:underline">
                {l.label}
              </Link>
            ))}
            <LanguageSwitch />
            {user && (
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  {user.displayName} <Badge tone="neutral">{t(`roles.${user.role}`)}</Badge>
                </span>
                <Button variant="secondary" onClick={() => void handleLogout()}>
                  {t('auth.logout')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobil: Sprache bleibt sichtbar; Hamburger nur, wenn es Navigationslinks gibt. */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitch />
            {user && (
              <button
                type="button"
                aria-label={t('nav.menu')}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-slate-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-slate-700/60"
              >
                <span aria-hidden="true" className="text-xl leading-none">
                  {menuOpen ? '✕' : '☰'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Mobiles, ausklappendes Navigationspanel */}
        {user && menuOpen && (
          <div id="mobile-menu" className="border-t border-slate-200/70 px-6 py-3 md:hidden dark:border-slate-700/70">
            <nav className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-2 py-2 text-sm font-medium hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="mt-3 flex items-center justify-between border-t border-slate-200/70 pt-3 dark:border-slate-700/70">
              <span className="text-sm">
                {user.displayName} <Badge tone="neutral">{t(`roles.${user.role}`)}</Badge>
              </span>
              <Button variant="secondary" onClick={() => void handleLogout()}>
                {t('auth.logout')}
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <CreditFooter text={appConfig?.creditName ?? ''} />
    </div>
  );
}
